import React, { lazy } from 'react';
import { Target, Zap, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
export function GuaranteesSection() {
  return <section className="py-24 bg-[#050505] relative overflow-hidden border-y border-gray-900" data-id="element-2546">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] opacity-50 pointer-events-none" data-id="element-2547" />

      <div className="max-w-6xl mx-auto px-4 relative z-10" data-id="element-2548">
        <div className="text-center mb-20" data-id="element-2549">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6 uppercase tracking-tight" data-id="element-2550">
            Our <span className="text-[#FFD700]" data-id="element-2551">Guarantees</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto font-mono" data-id="element-2552">
            Protocol verified. Standards enforced. Peace of mind secured.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-2553">
          {/* Feature Image with Forensic Frame */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="order-2 md:order-1 relative group" data-id="element-2554">
            {/* Image Container */}
            <div className="relative p-2 border border-[#FF0000]/30 bg-[#0a0a0a]" data-id="element-2555">
              {/* Corner Markers */}
              <div className="absolute -top-1 -left-1 w-6 h-6 border-t-2 border-l-2 border-[#FF0000]" data-id="element-2556" />
              <div className="absolute -top-1 -right-1 w-6 h-6 border-t-2 border-r-2 border-[#FF0000]" data-id="element-2557" />
              <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-2 border-l-2 border-[#FF0000]" data-id="element-2558" />
              <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-2 border-r-2 border-[#FF0000]" data-id="element-2559" />

              <div className="relative overflow-hidden" data-id="element-2560">
                <img src="https://cdn.magicpatterns.com/uploads/tSzCSyjyTRy7F8NiAfyqPU/1765971970467_2.jpg" alt="Precision Technology and Lightning Speed" className="w-full h-auto grayscale hover:grayscale-0 transition-all duration-700 opacity-80 hover:opacity-100" loading="lazy" decoding="async" data-id="element-2561" />

                {/* Scan Line Overlay */}
                <div className="absolute top-0 left-0 w-full h-[2px] bg-[#FF0000] shadow-[0_0_15px_#FF0000] animate-[scan_4s_ease-in-out_infinite] opacity-50" data-id="element-2562" />

                {/* HUD Overlay Elements */}
                <div className="absolute top-4 left-4 flex gap-2" data-id="element-2563">
                  <span className="bg-black/70 border border-[#FF0000]/50 text-[#FF0000] text-[10px] font-mono px-2 py-1" data-id="element-2564">
                    REC ●
                  </span>
                  <span className="bg-black/70 border border-gray-700 text-gray-300 text-[10px] font-mono px-2 py-1" data-id="element-2565">
                    ISO 800
                  </span>
                </div>
              </div>
            </div>

            {/* Decorative Background Blob */}
            <div className="absolute -inset-4 bg-[#FF0000]/5 blur-2xl -z-10 rounded-full opacity-50" data-id="element-2566" />
          </motion.div>

          {/* Guarantees List */}
          <div className="space-y-6 order-1 md:order-2" data-id="element-2567">
            {/* Precision Technology */}
            <GuaranteeCard icon={<Target size={24} data-id="element-2569" />} title="Forensic Precision" description="Our algorithms are calibrated to detect subtext, slang, and context that general AI misses. We don't just read words; we read intent." delay={0.2} data-id="element-2568" />

            {/* Lightning Speed */}
            <GuaranteeCard icon={<Zap size={24} data-id="element-2571" />} title="Real-Time Processing" description="From upload to verdict in seconds. Anxiety doesn't wait, and neither should you. Get immediate clarity when you need it most." delay={0.4} data-id="element-2570" />

            {/* Accuracy */}
            <GuaranteeCard icon={<CheckCircle2 size={24} data-id="element-2573" />} title="99.9% Uptime Reliability" description="Always available for crisis moments. Our systems are redundant and monitored 24/7 to ensure we're there when you spiral." delay={0.6} data-id="element-2572" />
          </div>
        </div>
      </div>
    </section>;
}
const GuaranteeCard = ({
  icon,
  title,
  description,
  delay
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}) => {
  return <motion.div initial={{
    opacity: 0,
    x: 50
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5,
    delay
  }} className="bg-[#0a0a0a] p-6 border-l-2 border-gray-800 hover:border-[#FF0000] transition-colors group relative" data-id="element-2574">
      <div className="flex items-start gap-5" data-id="element-2575">
        <div className="flex-shrink-0 w-12 h-12 bg-[#FF0000]/5 border border-[#FF0000]/20 flex items-center justify-center text-[#FF0000] group-hover:bg-[#FF0000] group-hover:text-white transition-all duration-300" data-id="element-2576">
          {icon}
        </div>
        <div className="flex-1 min-w-0" data-id="element-2577">
          <h3 className="font-bold text-xl text-white mb-2 group-hover:text-[#FF0000] transition-colors uppercase tracking-wide" data-id="element-2578">
            {title}
          </h3>
          <p className="text-gray-400 text-sm leading-relaxed font-mono" data-id="element-2579">
            {description}
          </p>
        </div>
      </div>

      {/* Active Indicator */}
      <div className="absolute top-4 right-4 w-1.5 h-1.5 bg-[#FF0000] rounded-full opacity-0 group-hover:opacity-100 animate-pulse transition-opacity" data-id="element-2580" />
    </motion.div>;
};